import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { ChevronRight, CheckCircle, RadioButtonUnchecked, Pending, Error as ErrorIcon } from '@mui/icons-material';
import { useAiChatServices, useAiChatSessionStore } from '../AiChatContext';
import type { TodoList, TodoItem } from '@lib/models/todoListModel';
import Stack from '@mui/material/Stack';
import { Collapse, IconButton } from '@mui/material';
import LinerBackgroundProgress from '@lib/components/Library/LinearBackgroundProgress';

interface TaskListProps {
  forceExpanded?: number;
}

function StatusIcon({ status }: { status: TodoItem['status'] }) {
  switch (status) {
    case 'completed':
      return <CheckCircle sx={{ fontSize: '0.95rem' }} />;
    case 'in_progress':
      return <Pending sx={{ fontSize: '0.95rem' }} />;
    case 'failed':
      return <ErrorIcon sx={{ fontSize: '0.95rem' }} />;
    default:
      return <RadioButtonUnchecked sx={{ fontSize: '0.95rem' }} />;
  }
}

function statusColor(status: TodoItem['status']): string {
  switch (status) {
    case 'completed':
      return 'success.main';
    case 'in_progress':
      return 'primary.main';
    case 'failed':
      return 'error.main';
    default:
      return 'text.disabled';
  }
}

export const TaskList: React.FC<TaskListProps> = ({ }) => {
  const [isExpanded, setIsExpanded] = useState<boolean | undefined>(true);
  const lastToolEvent = useAiChatSessionStore((state) => state.lastToolEvent);
  const { agentGatewayService } = useAiChatServices();

  if (lastToolEvent?.eventName !== 'todolistUpdated' || lastToolEvent?.subAgentId) {
    return null;
  }

  // const todoList: TodoList = {
  //   items: [
  //     {
  //       key: "1",
  //       label: "item2 item2 item2 item2 item2",
  //       status: "completed",
  //       detail: "andandoandandoa ndandoandandoandand oandandoandand oandandoandandoandando andandoandandoandandoandandoandan doandandoandandoandandoandandoa ndandoandandoandandoandandoandandoandandoandandoandandoa ndandoandandoandandoandandoandandoandandoandando"
  //     },
  //     {
  //       key: "2",
  //       label: "item2",
  //       status: "in_progress",
  //       detail: "andando andando andando andando andando andando andando andando andando andando andando andando andando andando andando "
  //     },
  //     {
  //       key: "3",
  //       label: "item2",
  //       status: "in_progress",
  //       detail: "andando"
  //     },
  //   ],
  //   currentWorkingItemKey: "1"
  // }

  if (lastToolEvent.eventData == '' && !lastToolEvent.subAgentId) {
    return null;
  }

  const todoList: TodoList = JSON.parse(lastToolEvent.eventData);

  const currentInteraction = agentGatewayService.getCurrentInteraction();
  if (agentGatewayService && currentInteraction)
    currentInteraction.todoList = todoList;

  const completedCount = todoList.items.filter(item => item.status === 'completed').length;
  const totalCount = todoList.items.length;
  const position = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;
  const oneOfN = completedCount > 0 ? `${completedCount} de ${totalCount} completos` : 'Iniciando...'
  const tasksSummary = `${oneOfN} - ${position.toFixed(0)}%`;

  if (totalCount == 0) return null;


  const currentItem = todoList.currentWorkingItemKey
    ? todoList.items.find(i => i.key === todoList.currentWorkingItemKey)
    : null;
  const onGoingSummary = currentItem
    ? `${currentItem.label}${currentItem.detail ? `: ${currentItem.detail}` : ''}`
    : '';

  return (<Box
    sx={{
      overflow: 'hidden',
      fontSize: '0.85rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      p: 0,
      position: 'relative'
    }}
  >
    <Stack gap={isExpanded ? 1 : 0} sx={{ display: 'inline-flex', width: '100%' }}>

      <Box sx={{ '&:hover': { cursor: "pointer" } }} onClick={() => setIsExpanded(!isExpanded)}>
        <LinerBackgroundProgress value={position} completedColor='rgba(120,120,120,0.3)' unCompletedColor='rgba(120,120,120,0.1)'>
          <Stack direction={'row'} sx={{ width: '100%', flexWrap: 'wrap', display: 'inline-flex', mb: isExpanded ? 0.5 : 1, alignItems: 'center' }}>
            <Box sx={{ overflow: 'clip', minWidth: '18%', maxWidth: '18%', textWrap: 'nowrap', pl: 0.5, borderBottom: 'none' }}>
              <Typography
                variant="body1"
                sx={{
                  color: 'text.primary'
                }}
              >
                {tasksSummary}
              </Typography>
            </Box>
            <Box sx={{ minWidth: '55%', maxWidth: '55%', px: 1, mx: 1, pl: 0.5, borderBottom: 'none' }}>
              <Typography
                variant="body1"
                noWrap
                sx={{
                  color: 'primary.main'
                }}
              >
                {onGoingSummary}
              </Typography>
            </Box>

            <Box sx={{ minWidth: '23%', maxWidth: '23%', overflow: 'clip', textAlign: 'end', justifyContent: 'end', flexWrap: 'wrap' }}>
              <Stack direction="row" sx={{ width: '100%', display: 'inline-flex', overflow: 'clip', alignItems: 'center', justifyContent: 'end', flexWrap: 'nowrap' }} gap={0.5}>
                <Typography noWrap variant='body1' sx={{
                  color: 'text.primary'
                }}>{todoList.title?.length ?? 0 > 0 ? todoList.title : "Tarefas planejadas"}</Typography>
                <IconButton size='small'>
                  {isExpanded ? <ChevronRight fontSize='small' sx={{ rotate: '90deg' }} /> : <ChevronRight fontSize='small' sx={{ rotate: '-90deg' }} />}
                </IconButton>
              </Stack>
            </Box>
          </Stack>

        </LinerBackgroundProgress>
      </Box>


      <Collapse in={isExpanded} sx={{ maxHeight: '150px', overflow: 'auto', display: 'inline-flex' }}>

        <Stack gap={1} sx={{ display: 'inline-flex' }}>
          {todoList.items.map((item: TodoItem) => (
            <Box
              key={item.key}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 0.75,
                py: 0.4,
                opacity: item.status === 'completed' ? 0.5 : 1,
              }}
            >
              <Box sx={{ mt: '1px', flexShrink: 0, color: statusColor(item.status) }}>
                <StatusIcon status={item.status} />
              </Box>
              <Box>
                <Typography variant="caption" sx={{ display: 'block', fontSize: '0.78rem', lineHeight: 1.3, textDecoration: item.status === 'completed' ? 'line-through' : 'none' }}>
                  {item.label}
                </Typography>
                {item.detail && (
                  <Typography variant="caption" sx={{ display: 'block', fontSize: '0.7rem', color: 'text.secondary', lineHeight: 1.3 }}>
                    {item.detail}
                  </Typography>
                )}
              </Box>
            </Box>
          ))}
        </Stack>
      </Collapse>
    </Stack>

  </Box>

  );
};
